import * as T from 'three';
import {deathRandom,type DeathContactResolver} from './deathPhysics';

interface Piece {
  position:T.Vector3;previous:T.Vector3;rotation:T.Quaternion;previousRotation:T.Quaternion;
  velocity:T.Vector3;spin:T.Vector3;scale:T.Vector3;center:T.Vector3;corners:T.Vector3[];
}
const STEP=1/120;
/** The ten original rigid instances, captured without a pose change. Their
 * bounding corners collide with the same world as the corpse, at fixed cadence. */
export class CrystalWingDebris {
  readonly pieces:Piece[]=[];
  private accumulated=0;
  private readonly corner=new T.Vector3();
  private readonly before=new T.Vector3();
  private readonly corrected=new T.Vector3();
  private readonly normal=new T.Vector3();
  private readonly contactNormal=new T.Vector3();
  private readonly axis=new T.Vector3();
  private readonly rotation=new T.Quaternion();
  private readonly origin=new T.Vector3();
  constructor(matrices:readonly T.Matrix4[],bounds:T.Box3,velocity:T.Vector3,impact:T.Vector3,seed:number){
    const random=deathRandom(seed^0x4f35a971),center=bounds.getCenter(new T.Vector3()),kick=impact.clone().clampLength(0,3);
    for(let i=0;i<matrices.length;i++){
      const position=new T.Vector3(),rotation=new T.Quaternion(),scale=new T.Vector3();matrices[i].decompose(position,rotation,scale);
      if(Math.min(scale.x,scale.y,scale.z)<1e-8)rotation.identity();
      const corners:T.Vector3[]=[];
      for(const x of [bounds.min.x,bounds.max.x])for(const y of [bounds.min.y,bounds.max.y])for(const z of [bounds.min.z,bounds.max.z])corners.push(new T.Vector3(x,y,z).sub(center).multiply(scale));
      const centroid=center.clone().multiply(scale);position.add(centroid.clone().applyQuaternion(rotation));
      const separation=new T.Vector3((random()-.5)*.6,(random()-.5)*.3,(random()-.5)*.6);
      this.pieces.push({position,previous:position.clone(),rotation,previousRotation:rotation.clone(),scale,center:centroid,corners,
        velocity:velocity.clone().add(kick).add(separation),spin:new T.Vector3(random()-.5,random()-.5,random()-.5).multiplyScalar(2.8)});
    }
  }
  matrix(index:number,out:T.Matrix4):T.Matrix4{
    const piece=this.pieces[index];this.origin.copy(piece.center).applyQuaternion(piece.rotation).negate().add(piece.position);
    return out.compose(this.origin,piece.rotation,piece.scale);
  }
  step(dt:number,contact:DeathContactResolver):void{
    if(!Number.isFinite(dt)||dt<=0)return;
    this.accumulated+=Math.min(dt,.25);
    while(this.accumulated+1e-10>=STEP){
      for(const piece of this.pieces){
        piece.previous.copy(piece.position);piece.previousRotation.copy(piece.rotation);
        piece.velocity.multiplyScalar(Math.exp(-.08*STEP));piece.velocity.y-=18*STEP;
        piece.position.addScaledVector(piece.velocity,STEP);
        const spin=piece.spin.length();if(spin>1e-6){this.rotation.setFromAxisAngle(this.axis.copy(piece.spin).divideScalar(spin),spin*STEP);piece.rotation.premultiply(this.rotation).normalize();}
        this.contactNormal.set(0,0,0);let contacts=0;
        for(const corner of piece.corners){
          this.before.copy(corner).applyQuaternion(piece.previousRotation).add(piece.previous);
          this.corner.copy(corner).applyQuaternion(piece.rotation).add(piece.position);this.corrected.copy(this.corner);
          if(contact(this.before,this.corrected,.008,this.normal)){
            piece.position.add(this.corrected.sub(this.corner));this.contactNormal.add(this.normal);contacts++;
          }
        }
        if(contacts>0){
          this.contactNormal.normalize();const inward=piece.velocity.dot(this.contactNormal);
          if(inward<0)piece.velocity.addScaledVector(this.contactNormal,-inward+Math.min(1.1,-inward*.12));
          piece.velocity.multiplyScalar(Math.exp(-8*STEP));piece.spin.multiplyScalar(Math.exp(-10*STEP));
        }
      }
      this.accumulated-=STEP;
    }
  }
}
